<template>
  <section v-if="hasData" id="trust" class="trust" aria-labelledby="trust-title">
    <div class="trust-bg" aria-hidden="true"></div>

    <div class="trust-inner">
      <header class="trust-head" data-reveal="up">
        <span class="trust-eyebrow">{{ $t('landing.trust_eyebrow') }}</span>
        <h2 id="trust-title" class="trust-title">{{ $t('landing.trust_title') }}</h2>
        <p class="trust-sub">{{ $t('landing.trust_sub') }}</p>
      </header>

      <!-- Totals — real, live counts. The headline proof. -->
      <div class="trust-stats" data-reveal="up">
        <div class="trust-stat">
          <span class="trust-stat-num">{{ fmt(data.totals.labels) }}</span>
          <span class="trust-stat-label">{{ $t('landing.trust_stat_labels') }}</span>
        </div>
        <div class="trust-stat">
          <span class="trust-stat-num">{{ fmt(data.totals.artists, true) }}</span>
          <span class="trust-stat-label">{{ $t('landing.trust_stat_artists') }}</span>
        </div>
        <div class="trust-stat">
          <span class="trust-stat-num">{{ fmt(data.totals.releases, true) }}</span>
          <span class="trust-stat-label">{{ $t('landing.trust_stat_releases') }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const { data } = await useFetch('/api/public/showcase', {
  key: 'landing-showcase',
  default: () => ({ totals: { labels: 0, artists: 0, releases: 0 } })
})

// Gated on the totals, not on the label/artist lists: those lists are no
// longer rendered here, so keying visibility off them would blank the whole
// section — headline numbers included — on a tenant that has counts but no
// showcase-eligible logos or avatars. Same condition as the hero trustline.
const hasData = computed(() => {
  const t = data.value?.totals || {}
  return (t.labels || 0) > 0 || (t.artists || 0) > 0 || (t.releases || 0) > 0
})

// Round-and-suffix for headline counts: exact small numbers, "1.8K+" style
// for large ones so the section reads as scale without implying false
// precision. Kept identical to the hero trustline's copy of this.
function fmt(n, approx = false) {
  const v = Number(n) || 0
  if (!approx || v < 1000) return String(v)
  if (v < 10000) return (Math.floor(v / 100) / 10).toFixed(1).replace(/\.0$/, '') + 'K+'
  return Math.floor(v / 1000) + 'K+'
}
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.trust {
  position: relative;
  padding: var(--landing-section-pad) 0;
  overflow: hidden;
}
.trust-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 50% 0%, rgb(var(--primary-channels) / 0.08), transparent 70%),
    radial-gradient(ellipse 50% 40% at 100% 100%, rgb(var(--secondary-channels) / 0.06), transparent 70%);
  pointer-events: none;
}
.trust-inner {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.trust-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.trust-eyebrow {
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: 16px;
}
.trust-title {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 16px;
}
.trust-sub {
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

/* ── Totals ── */
.trust-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
}
.trust-stat {
  flex: 1 1 180px;
  max-width: 260px;
  text-align: center;
  padding: 28px 20px;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  background: rgb(255 255 255 / 0.015);
  backdrop-filter: blur(6px);
}
.trust-stat-num {
  display: block;
  font-family: var(--font-heading);
  font-size: clamp(2.4rem, 5vw, 3.4rem);
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.trust-stat-label {
  display: block;
  margin-top: 10px;
  font-size: 0.82rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}
</style>
